import { createContext, useContext, useEffect } from 'react';

/** Set inside the one-page chart: the key of the section being drawn. The sections draw their own heading in the
 *  section role and their sub-headings quieter. Scoped by context rather than by a switch, so the inner-tab
 *  chart (tests, stories) never changes. */
export const ChartPageContext = createContext<string | null>(null);

/** The key of the section this component sits in, or null outside the one-page chart. */
export function useChartPageSection(): string | null {
  return useContext(ChartPageContext);
}

export function useInChartPage(): boolean {
  return useContext(ChartPageContext) !== null;
}

/** The heading a tab body draws that IS its section's name. The page already titles the section with the full
 *  name, so these headings drop their text and keep only their actions (add, links). Any other heading in a
 *  section (Active problems, Future appointments…) is a sub-heading and stays. */
export const OWN_SECTION_TITLES: Record<string, readonly string[]> = {
  allergies: ['Allergies'],
  procedures: ['Procedures'],
  'drug-interactions': ['Drug interactions'],
  immunizations: ['Immunizations'],
  documents: ['Documents'],
  notes: ['Notes'],
  flags: ['Flags'],
};

/** The DOM id of a section of the one-page chart (the index and the summary cards scroll to it). */
export const sectionDomId = (key: string) => `chart-section-${key}`;

/** Set by the one-page chart: how a section tells the index how many rows it holds (the count beside its entry). */
export const ChartCountContext = createContext<((key: string, count: number | null) => void) | null>(null);

/** A section reports its row count to the index (`null` while it loads: no count yet). Nothing outside the one-page chart. */
export function useReportChartCount(count: number | null): void {
  const key = useChartPageSection();
  const report = useContext(ChartCountContext);
  useEffect(() => {
    if (key && report) report(key, count);
  }, [key, report, count]);
}
